import { useState, useCallback } from 'react';
import { CompanyBilling, PlanUsage } from '../types';
import { getCompanyBilling, getCurrentPlanUsage, updateAiSpendingCap } from '../services/storageService';
import { requireCompanyId } from './requireCompanyId';

export function useBilling(companyId: string | null) {
  const [billing, setBilling] = useState<CompanyBilling | null>(null);
  const [usage, setUsage] = useState<PlanUsage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runAction = useCallback(async <T>(action: (cid: string) => Promise<T>): Promise<T> => {
    setLoading(true);
    setError(null);
    try {
      const cid = requireCompanyId(companyId);
      return await action(cid);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unexpected error';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  const load = useCallback(async () => {
    await runAction(async (cid) => {
      const [b, u] = await Promise.all([
        getCompanyBilling(cid),
        getCurrentPlanUsage(cid),
      ]);
      setBilling(b);
      setUsage(u);
    });
  }, [runAction]);

  const setSpendingCap = useCallback(async (cap: number) => {
    const cid = requireCompanyId(companyId);
    await updateAiSpendingCap(cid, cap);
    await load();
  }, [companyId, load]);

  const isReadOnly = billing?.plan === 'expired' || billing?.plan === 'cancelled';
  const isTrialExpired = billing?.plan === 'expired';
  const trialDaysLeft = billing?.trialEndsAt
    ? Math.max(0, Math.ceil((new Date(billing.trialEndsAt).getTime() - Date.now()) / 86_400_000))
    : null;

  return {
    billing,
    usage,
    loading,
    error,
    load,
    setSpendingCap,
    isReadOnly,
    isTrialExpired,
    trialDaysLeft,
  };
}
